// src/component/datatable2/models/FilterDisplayValue.ts

/** Keys read from an option object, in priority order. */
export type DisplayKey = 'id' | 'label' | 'name';

/** Text shown for a filter value (inline header inputs, the text filter panel). */
export class FilterDisplayValue {
    /** Order used by the inline filter header. */
    static readonly HEADER_KEYS: readonly DisplayKey[] = ['label', 'name', 'id'];

    /** Order used for the text filter panel's search text. */
    static readonly PANEL_KEYS: readonly DisplayKey[] = ['label', 'id', 'name'];

    /**
     * Arrays → the options' texts joined with ", "; objects → their first
     * defined key; null/undefined → ''; anything else → String(value).
     */
    static toText(value: unknown, keys: readonly DisplayKey[] = FilterDisplayValue.HEADER_KEYS): string {
        if (Array.isArray(value)) {
            return value
                .map(item => FilterDisplayValue.firstKey(item, keys) ?? item)
                .filter(Boolean)
                .map(String)
                .join(', ');
        }

        if (value && typeof value === 'object') {
            return String(FilterDisplayValue.firstKey(value, keys) ?? '');
        }

        return value == null ? '' : String(value);
    }

    /**
     * Search text the text filter panel opens with: the applied value
     * (string, chosen options or option), else the text typed in the header.
     */
    static panelSearchText(currentValue: unknown, typedText: unknown): string {
        if (typeof currentValue === 'string') {
            return currentValue;
        }

        if (Array.isArray(currentValue) || (currentValue && typeof currentValue === 'object')) {
            return FilterDisplayValue.toText(currentValue, FilterDisplayValue.PANEL_KEYS);
        }

        return typeof typedText === 'string' ? typedText : '';
    }

    private static firstKey(item: unknown, keys: readonly DisplayKey[]): unknown {
        if (item == null || typeof item !== 'object') {
            return undefined;
        }

        const record = item as Record<string, unknown>;

        for (const key of keys) {
            if (record[key] !== null && record[key] !== undefined) {
                return record[key];
            }
        }

        return undefined;
    }
}
